import { AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { Subscription, Observable } from 'rxjs';
import { DateService } from 'src/app/shared/services/date.service';
import { GetOpenEnrollmentRule } from '../../family-form/types/qualifying-life-event.response.interface';
import { setProxyValue } from '../../plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer';
import { defaultDateOfLifeEventProxyConnectPath, submittedAttemptProxyConnectPath,defaultLifeEventProxyConnectPath } from 'src/app/angular-elements/shared/interfaces/special-enrollment-circumstances.interface';
import { FamilyFormService } from '../../family-form/services/family-form.service';
import { SepLifeEventService } from 'src/app/shared/services/sep-life-event.service';
import { setLifeEventDate } from 'src/app/shared/reducers/sep-life-event-form.reducer';
import { setFetchFormConfig } from 'src/app/shared/reducers/sep-life-event-form.reducer';

interface ComponentFormControls {
  dateOfLifeEvent: AbstractControl;
}

interface ComponentFormValuesAsJson {
  dateOfLifeEvent: moment.Moment;
}

/**
 * This component serves as the date of life event control for the new ( 2025 ) special enrollment workflow.
 * 
 *  Because we have existing state structure {
 *    isApplicableCircumstance: boolean;
 *    lifeEvent: LifeEvent;
 *    dateOfLifeEvent: ...
 *  }
 * 
 * Which we want to continue to make use of without impacting existing functionality, this component will serve as a proxy....
 * 
 * The component will now be in charge of assigning all 3 state values at the same time:  upon successful submission of the date form
 * 
 * The date will be stored in redux and will be consumed in the page- component:
 * 1) which triggers manual events 
 * 2) which will tell the form the values to set for isApplicableCircumstance and lifeEvent ( these are stored in the forms-as-json as default values )
 * 3) which will pick-up the date value from redux temp state and attach it to the dateOfLifeEvent from the forms-as-json
 * 
 * Why do it this way?
 * ShopX has a gap due to storing all of our data in 1 state store without differentiation of activity ( think: blahblah.com?applicationid=1 ), 
 * where a user can set values and skip to later in the flow without appropriately confirming the validation was correct.
 * 
 * Example:
 * 2 flows - one flow requires you to enter a DOB before 1950, the other requires you to enter DOB after 1950
 * Assuming you begin flow and appropriately enter a date of 1930, 
 * You navigate far enough into the app that you wish to submit
 * But then you open a second tab which houses the same data - you change to flow 2 where validation logic allows you to enter 1970 and invalidates 1930
 * Now you manually navigate (avoiding the page form-submission step which may say "This user is confirmed in flow 2") 
 *   and inappropriately submit the app with the wrong date.
 * 
 * In the above example, the user managed to take advantage of the store limitation.
 * 
 * This component will help to alleviate that pain by ensuring that all data is temporarily set until such a time that the user confirms
 *   via form validation and submission button pressing
 * That only when it is a confirmed change of flow does the data get overridden.
 * 
 * This isn't perfect - one can still submit this form and navigate to the end of their flow
 * But it will ensure that only the pressing of the submit button is the NEW data the CURRENT data
 * 
 */
@FormComponentLookup('special-enrollment-date-of-life-event-proxy')
@Component({
  selector: 'app-special-enrollment-date-of-life-event-proxy',
  templateUrl: './special-enrollment-date-of-life-event-proxy.component.html',
  styleUrls: ['./special-enrollment-date-of-life-event-proxy.component.scss'],
  standalone: false
})
export class SpecialEnrollmentDateOfLifeEventProxyComponent extends FormEntryComponentClass implements OnInit, AfterContentInit, OnDestroy {

  dateForm: FormGroup;
  dateFormSubscription: Subscription;

  lifeEvent: GetOpenEnrollmentRule;
  proxyLifeEventConnectPath: string;
  proxyDateOfLifeEventConnectPath: string;

  submitAttemptedObservable$: Observable<boolean>;
  submitAttemptedSubscription: Subscription;

  constructor(
    private dateService: DateService,
    private fb: FormBuilder,
    private familyFormService: FamilyFormService,
    private sepLifeEventService: SepLifeEventService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.ngRedux.dispatch(setProxyValue(submittedAttemptProxyConnectPath, false));

    if(!this.entry?.formControlAttributes?.formControlName || !this.entry?.formControlAttributes?.connectPath){
      throw new Error("Error in form config - no connect defined for Entry!")
    }

    if(!this.currentPage?.custom?.lifeEventObj){
      this.proxyLifeEventConnectPath = this.currentPage?.custom?.proxyLifeEventConnectPath ?? defaultLifeEventProxyConnectPath;
    }

    this.proxyDateOfLifeEventConnectPath = this.currentPage?.custom?.proxyDateOfLifeEventConnectPath ?? defaultDateOfLifeEventProxyConnectPath;

    // Custom controls which exists only to validate an emitted event
    // This one is listened to by the date field
    this.form.addControl("SXEANG_PROXY_LIFE_EVENT_DATE_TRIGGER", this.fb.control(true));

    this.dateForm = this.fb.group<any>({
      [this.entry.formControlAttributes.formControlName]: this.fb.control<string>(null, {
        validators: [this.validate(), Validators.required]
      })
    });

    // Proxy behavior - if the proxy form control is good, set the value to temp redux state storage and update forms-as-json control validity
    this.dateFormSubscription = this.dateForm.valueChanges.subscribe( (value: any) => {
      if (this.dateForm.valid) {

        // Moment format
        const date = value[this.entry.formControlAttributes.formControlName];
        
        // Store value to be consumed by the PAGE- component later ( this will assign the value to the parent date )
        this.ngRedux.dispatch(setProxyValue(this.proxyDateOfLifeEventConnectPath, date));
        
        // We have a valid date saved, therefore, the parent date will be set valid to allow for page submission logic to run
        this.forceParentDateValidity(true);
      } else {
        this.forceParentDateValidity(false);
      }
    });

    this.submitAttemptedObservable$ = this.ngRedux.select(['payloadStates', 'special-enrollment-circumstance', submittedAttemptProxyConnectPath]);
    this.submitAttemptedSubscription = this.submitAttemptedObservable$.subscribe((submitAttempted: boolean) => {
      if(submitAttempted){
        this.dateForm.controls[this.entry.formControlAttributes.formControlName].markAsTouched();
        this.dateForm.controls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
        if(this.dateForm.errors){
          this.forceParentDateValidity(false);
        }
      }
    })


    this.setDynamicAttributes();
  }

  ngAfterContentInit(): void {
    // Initially ensure the form itself is invalid so that we cannot navigate until the proxy behavior has completed  
    // Timeout to get out of the forms-as-json initial validation
    
    setTimeout(() => {
      this.ngRedux.dispatch(setProxyValue(this.proxyDateOfLifeEventConnectPath, null));
      this.forceParentDateValidity(false)
    }, 0)
  }
  
  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.dateFormSubscription) {
      this.dateFormSubscription.unsubscribe();
    }
    if(this.submitAttemptedSubscription){
      this.submitAttemptedSubscription.unsubscribe();
    }
  }

  get dateFormControls(): ComponentFormControls {
    return this.dateForm?.controls as unknown as ComponentFormControls;
  }

  dateChanged(type: any, event: any) {
    this.dateForm.controls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
  }

  onDateSelected(dateValue: string): void {
      // Store in Redux
      this.ngRedux.dispatch(setLifeEventDate(dateValue));
  }

  get componentFormControls(): ComponentFormControls {
    return this.formControls as unknown as ComponentFormControls;
  }
  get componentFormValuesAsJson(): ComponentFormValuesAsJson {
    return this.form.value as unknown as ComponentFormValuesAsJson;
  }

  validate(): ValidatorFn {
    return (control: AbstractControl) => {
      const payloadStates = this.reducerUtilityService.getPayloadStates();
      const lifeEventValue = this.currentPage?.custom?.lifeEventObj ?? payloadStates?.['special-enrollment-circumstance']?.[this.proxyLifeEventConnectPath];
      const lifeEventDateControl = this.dateFormControls?.dateOfLifeEvent;

      // Control not yet ready for validation
      if (!lifeEventDateControl) { return null; }

      const lifeEventDateValue: moment.Moment = lifeEventDateControl.value ? this.dateService.getDateAsMoment(lifeEventDateControl.value) : null;
      
      let isValid = true;
      const errors = {
        rangeError: false,
        firstOfMonthError: false
      }

      /** Range */
      if (lifeEventValue && lifeEventDateValue) {

        errors.rangeError = !this.isValidEnrollmentDate(
          lifeEventDateValue,
          lifeEventValue.startDateRuleDays,
          lifeEventValue.endDateRuleDays
        );
        if (errors.rangeError) {
          isValid = false;
        }

        const isEventOnFirstOfMonth = lifeEventDateValue.date() === 1;
        /** Custom */
        if (this.entry.custom?.dateMustBeFirstOfMonth && !isEventOnFirstOfMonth) {
          errors.firstOfMonthError = true;
          isValid = false;
        }

      } else {
        isValid = false;
      }

      if (isValid) {
        return null;
      } else {
        if (errors.firstOfMonthError) {
          return { firstOfMonth: true };
        }
        if (errors.rangeError) {
          return { outOfRange: true };
        }
      }
    }
  }

  /**
   * 
   * @param dateToCheck - life event date
   * @param endOfDateWindow # of days BEFORE current date that event-date is valid
   * @param beginningOfDateWindow - # of days AFTER current date that event-date still valid
   * @returns 
   */
  isValidEnrollmentDate(dateToCheck: moment.Moment, endOfDateWindow: string, beginningOfDateWindow: string) {

    if (
      !(endOfDateWindow?.length > 0) &&
      !(beginningOfDateWindow?.length > 0)
    ) {
      return false;
    } else {

      const lastValidDateInWindow: moment.Moment = this.dateService.getTodayAsMoment();
      const firstValidDateInWindow: moment.Moment = this.dateService.getTodayAsMoment();

      if (endOfDateWindow && beginningOfDateWindow) {
        lastValidDateInWindow.date(lastValidDateInWindow.date() + parseInt(endOfDateWindow, 10));
        firstValidDateInWindow.date(firstValidDateInWindow.date() - parseInt(beginningOfDateWindow, 10) - 1);

        return (lastValidDateInWindow >= dateToCheck && firstValidDateInWindow <= dateToCheck) ? true : false;

      } else if (beginningOfDateWindow) {

        firstValidDateInWindow.date(firstValidDateInWindow.date() - parseInt(beginningOfDateWindow, 10) - 1);

        return (lastValidDateInWindow >= dateToCheck && firstValidDateInWindow <= dateToCheck) ? true : false;

      } else {
        console.log('something wrong in the date');
        return false;
      }

    }
  }

  async setDynamicAttributes() {
    if (this.currentPage?.custom?.fetchFromConfig) {
      if(this.currentPage?.custom?.lifeEventObj){
        this.form.addControl("SXEANG_PROXY_LIFE_EVENT_TRIGGER", this.fb.control(true));
        this.lifeEvent = this.currentPage?.custom?.lifeEventObj as GetOpenEnrollmentRule;
        
      } else{
        const payloadStates = this.reducerUtilityService.getPayloadStates();
        this.lifeEvent = payloadStates?.['special-enrollment-circumstance']?.[this.proxyLifeEventConnectPath] as GetOpenEnrollmentRule;
      }
     } else {
        /** Get qualifying life event options */
        const response = await this.familyFormService.getOpenEnrollmentRules(this.currentPage?.custom?.lifeEventObj.lifeEventId);
        this.lifeEvent = response.responseData.dataElement.getOpenEnrollmentRules[0];
      }
      this.lifeEvent.asOfDate = this.dateService.getTodayAsMoment();
      this.ngRedux.dispatch(setFetchFormConfig(this.currentPage?.custom?.fetchFromConfig ?? true));
      this.sepLifeEventService.findLifeEventRule([this.lifeEvent] as GetOpenEnrollmentRule[]);
  }

  /**
   * Because we're using this component as a proxy, we need to ensure our actual parent formControl isn't processed separately.
   * Thus we bind its fate to that of the logic in this form:  if this form doesn't have a valid value, neither does the parent form
   * 
   * @param shouldForceValid False forces the control invalid
   */
  forceParentDateValidity(shouldForceValid: boolean) {
    if (!shouldForceValid) {
      this.form.controls[this.entry.formControlAttributes.formControlName].setErrors({ 'forced_invalid': true });
    } else {
      const currentErrors = this.form.controls[this.entry.formControlAttributes.formControlName].errors;
      if (currentErrors) {
        delete currentErrors['forced_invalid'];
      }
      this.form.controls[this.entry.formControlAttributes.formControlName].setErrors(currentErrors);
      this.form.controls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
    }
  }

}
